// Раунд спина: от нажатия кнопки до возврата в ожидание.
//
// В раунде не один спин, а сколько прислал бэкенд: за оплаченным спином идут бесплатные
// респины, см. Nudge Respin в GDD, раздел 4.3. Все они приходят одним ответом, и раунд играет
// их подряд, не спрашивая бэкенд снова.
//
// Каждый спин идёт через два состояния игры. На spinning барабаны крутятся, а игра ждёт
// выдержку и, на первом спине, ответ бэкенда. На result барабаны доезжают до итога, играют
// анимации выигрыша и всплывает сумма. Оба состояния разделены одним мигом: заказом остановки.
//
// Показ итога и показ надстройки идут разом, а не по очереди: выигрыш играет на поле,
// а надстройка — над ним, и смотрят на них одними глазами.
//
// Крупный выигрыш раунд называет сам, после показа: сумма в поле для него не всплывает.
// Вместо неё поверх игры встаёт попап с манометром Big Win, а за ним сходятся створки
// с надписью BIG WIN и счётчиком, см. GDD, раздел 3.7, и ADR-56.
//
// Перед первым респином раунд объявляет фичу: поле закрывается заглушкой с её именем
// и снова открывается. Объявление одно на раунд — цепочка респинов идёт от одной фичи.
//
// Кнопка меняет роль по ходу раунда, и правило у неё одно: пока раунду есть что торопить,
// на ней стоит скип. Нажатие проходит ровно одну ступень того, что идёт сейчас: выдержку
// прокрутки, доводку барабанов, показ итога или объявление фичи. Спин на кнопку возвращается
// на последнем спине раунда, когда барабаны встали: торопить остаётся только показ,
// и он спину не мешает.
//
// Заблокирована кнопка в двух случаях. Первый: игра ждёт ответ бэкенда, а торопить нечего,
// барабанам вставать не на что, пока итога нет. Второй: на счету меньше ставки, и крутить
// не на что вовсе, см. ADR-22.
//
// Ставку раунд берёт на входе и держит до конца: игрок волен двигать её кнопками полосы, пока
// барабаны крутятся, но платит он ту, которой начал. Списывает её бэкенд, а кошелёк показывает
// это сразу, не дожидаясь итога, см. game/wallet.ts.
//
// Выигрыш каждого спина раунд отдаёт кошельку сразу за показом этого спина. Остаток счёта
// он ставит по ответу бэкенда в самом конце, после показа надстройки: выплату бонуса приносит
// именно остаток, а бонус играет надстройка.
//
// Итог раунда клиент не выдумывает. Сорванный запрос транспорт повторяет сам, а когда и повторы
// не помогли, игрок видит сообщение и решает, повторять ли дальше. Барабаны всё это время
// крутятся: встать им не на что.

import type { Cents } from '../api/gameConfig';
import { spinPayout, type RoundResult, type SpinResult } from '../api/spin';
import { text } from '../i18n/text';
import { sfx, winSfx, type SfxName } from '../sound/sound';
import type { FieldCover } from '../ui/fieldCover';
import type { Notice } from '../ui/notice';
import type { ReelHold, ReelsView } from '../ui/reels';
import type { SpinControls } from '../ui/spinControls';
import type { WinShow } from '../ui/winShow';

import type { GameState } from './game';
import type { StateMachine } from './stateMachine';
import type { Wallet } from './wallet';

/**
 * Сколько барабаны крутятся вхолостую, даже если итог уже известен.
 *
 * Выдержка одна на оплаченный спин и на респин. Респин идёт по готовому итогу, но короче
 * от этого быть не должен: игрок читает его теми же глазами.
 */
export const HOLD_MS: number = 1500;

/**
 * Дошёл ли выигрыш спина до порога Big Win.
 *
 * Порог считается от ставки игрока: GDD, раздел 3.7, задаёт его кратностью общей ставки.
 * Слот без кратности крупные выигрыши не выделяет, и сумма всегда всплывает в середине поля.
 *
 * @param payout Выплата за спин.
 * @param stake Ставка, которой сыгран раунд.
 * @param rate Кратность общей ставки из описания игры. Null — порога у слота нет.
 */
export function isBigWin(payout: Cents, stake: Cents, rate: number | null): boolean {
  return rate !== null && rate > 0 && payout >= stake * rate;
}

/**
 * Надстройка раунда: она вклинивается между ступенями показа.
 *
 * Раунд зовёт её после показа итога каждого спина и ещё раз в конце раунда. Что она там
 * показывает, раунду не важно: он только ждёт её и даёт игроку пройти показ скипом.
 *
 * Слот без надстроек её не заводит вовсе, и раунд идёт как раньше.
 */
export interface RoundFeature {
  /**
   * Показывает, что надстройка сделала на этом спине. Раунд ждёт конца показа.
   *
   * Идёт он разом с показом итога спина, поэтому поле надстройка не трогает: там в эти же
   * секунды играют анимации выигрыша.
   */
  afterSpin(spin: SpinResult): Promise<void>;

  /** Показывает, чем кончился раунд: смену стадии и прочее, что меняется раз за раунд. */
  afterRound(round: RoundResult): Promise<void>;

  /** Проходит показ надстройки разом. */
  skip(): void;
}

/**
 * Показ крупного выигрыша поверх поля, см. ui/bigWin.ts.
 *
 * Раунд зовёт его перед створками и ждёт: попап уходит сам через пару секунд, игрока он
 * не держит. Слот без попапа его не заводит, и сумму называют одни створки.
 */
export interface RoundBigWin {
  /** Показывает сумму и ждёт, пока показ уйдёт. */
  show(caption: string, total: Cents): Promise<void>;

  /** Проходит показ разом. */
  skip(): void;
}

/**
 * Анимация ожидания: она тянет остановку одного барабана и играет над ним, см. GDD, раздел 3.5.
 *
 * Раунд не знает ни про скаттеры, ни про рамку: он спрашивает, тянуть ли этот спин,
 * и убирает показ, когда барабаны встали.
 */
export interface RoundAnticipation {
  /** Барабан, который тянет остановку на этом спине. Null — тянуть нечего. */
  reelFor(spin: SpinResult): number | null;

  /** Насколько дольше крутится барабан ожидания. */
  readonly seconds: number;

  /** Пускает показ на заданном барабане. */
  show(reel: number): void;

  /** Убирает показ с барабана. */
  hide(): void;

  /** Снимает показ разом. */
  skip(): void;
}

/** Всё, чем раунд спина двигает игру. */
export interface SpinParts {
  readonly machine: StateMachine<GameState>;
  readonly reels: ReelsView;
  readonly controls: SpinControls;
  readonly wins: WinShow;
  readonly cover: FieldCover;
  readonly notice: Notice;

  /** Надстройка раунда. Null — надстроек у слота нет. */
  readonly feature: RoundFeature | null;

  /** Анимация ожидания. Null — ждать в слоте нечего. */
  readonly anticipation: RoundAnticipation | null;

  /** Показ крупного выигрыша. Null — сумму называют одни створки. */
  readonly bigWin: RoundBigWin | null;

  /** Кошелёк игрока: с него уходит ставка и на него встаёт выигрыш раунда. */
  readonly wallet: Wallet;

  /** Кратность общей ставки, с которой идёт Big Win. Null — крупные выигрыши не выделяются. */
  readonly bigWinRate: number | null;

  /** Просит бэкенд сыграть раунд. Раунд повторяет запрос сам, пока не получит итог. */
  readonly ask: (round: string, bet: Cents) => Promise<RoundResult>;

  /** Спин снова доступен на показе последнего результата. Автоигра может сразу начать следующий. */
  readonly onSpinAvailable?: (() => void) | undefined;
}

/** Выдержка, которую можно оборвать досрочно. */
interface Countdown {
  /** Ждёт конца выдержки. */
  readonly finished: Promise<void>;

  /** Обрывает выдержку немедленно. */
  finish: () => void;
}

/** Заводит выдержку на заданное время. */
function countdown(ms: number): Countdown {
  let finish = (): void => {};

  const finished = new Promise<void>((resolve) => {
    const timer: ReturnType<typeof setTimeout> = setTimeout(resolve, ms);

    finish = (): void => {
      clearTimeout(timer);
      resolve();
    };
  });

  return { finished, finish };
}

/** Идущий раунд спина. */
export interface SpinRound {
  /** Ждёт конца раунда. */
  readonly finished: Promise<void>;

  /** Обрывает показ итога: барабаны падают, анимации снимаются. */
  cut(): void;
}

/** Крутит барабаны, останавливает их на итоге спина и показывает выигрыш. */
export function runSpinRound(parts: SpinParts): SpinRound {
  const { machine, reels, controls, wins, cover, notice, feature, anticipation, bigWin, wallet } = parts;

  // Ставка берётся на входе в раунд и держится до его конца: игрок волен двигать её кнопками,
  // пока барабаны крутятся, но платит он ту, которой начал.
  const bet: Cents = wallet.stake;

  let cut = false;

  return {
    finished: round(),
    cut: () => {
      cut = true;
      reels.drop();
      wins.stop();
      cover.skip();
      bigWin?.skip();
      feature?.skip();
      anticipation?.skip();
    },
  };

  async function round(): Promise<void> {
    machine.goTo('spinning');

    // Ставка уходит со счёта тем же движением, каким игрок нажал на кнопку. Списал её бэкенд,
    // тем же запросом, а полоса показывает это сразу, не дожидаясь итога.
    wallet.bet();

    // Прошлый выигрыш крутит свои линии до этой минуты. Досматривать его некогда: игрок
    // начал новый спин.
    wins.stop();

    reels.start();
    sfx('spin');

    // Что делает скип сейчас. null — торопить нечего, и кнопка заблокирована.
    let hurry: (() => void) | null = null;

    const offerSkip = (next: (() => void) | null): void => {
      hurry = next;
      controls.showSkip(next);
    };

    const hold: Countdown = countdown(HOLD_MS);

    // Скип живой с первого кадра раунда: игроку не надо ждать, чтобы им воспользоваться.
    // Нажатие обрывает выдержку и тем заказывает остановку наперёд.
    offerSkip(hold.finish);

    // Пришёл ли итог. Пока его нет, барабанам вставать не на что, сколько их ни торопи.
    let answered = false;

    const asked: Promise<RoundResult> = ask().then((result: RoundResult) => {
      answered = true;
      return result;
    });

    // Выдержка прошла, а итога нет: торопить нечего, и кнопка это честно показывает.
    // Барабаны доедут до итога сами, как только бэкенд ответит.
    void hold.finished.then(() => {
      if (!answered)
        offerSkip(null);
    });

    const [result]: [RoundResult, void] = await Promise.all([asked, hold.finished]);

    for (let index = 0; index < result.spins.length; index++) {
      const spin: SpinResult = result.spins[index];

      // Первый спин уже крутится: его выдержка шла разом с запросом.
      if (index > 0) {
        // Фича объявляется один раз за раунд: цепочка респинов — это одна фича, а не новая
        // на каждый спин.
        if (index === 1)
          await announce();

        await respin(spin);
      }

      // Барабаны пошли на остановку: с этого мига идёт показ итога. Скип остаётся на кнопке,
      // только теперь он роняет барабаны разом вместе с показом ожидания.
      machine.goTo('result');
      offerSkip(() => {
        reels.drop();
        anticipation?.skip();
      });

      await reels.stop(spin, anticipate(spin));

      // Барабан ожидания встал, и рамке над ним больше нечего ждать.
      anticipation?.hide();

      // Барабаны встали. На последнем спине торопить больше нечего, и кнопка отдаёт спин:
      // нажатие обрывает показ и начинает следующий раунд, см. SpinRounds. Пока респины
      // не кончились, кнопка остаётся скипом, и нажатие проходит показ этого спина вместе
      // с показом надстройки: для игрока это одна ступень.
      //
      // Раунд с бонусом спина не отдаёт вовсе. Бонус идёт следом и держит экран десятки
      // секунд: кнопка спина обещала бы игроку прокрутку, которой на ней ещё нет, а автоигра
      // нажала бы её сама и оборвала бонус на первом же кадре. Скипом кнопка стоит до конца
      // бонуса, а спин отдаёт конец раунда.
      if (index === result.spins.length - 1 && result.bonus === undefined) {
        controls.showSpin();
        parts.onSpinAvailable?.();
      } else {
        offerSkip(skipShow);
      }

      // Оборванный раунд ни показывать, ни продолжать нечем: следующий уже начался.
      if (cut)
        break;

      // Крупный выигрыш поле не называет: сумму показывают створки, см. GDD, раздел 3.7.
      const payout: Cents = spinPayout(spin);
      const big: boolean = isBigWin(payout, bet, parts.bigWinRate);

      // Показ итога и показ надстройки идут разом. Выигрыш играет на поле, а надстройка —
      // на мини-слоте и манометрах над ним: это разные места экрана, и ждать друг друга
      // им незачем. Идя по очереди, они оставляли бы между собой паузу, в которой игрок
      // досматривает доигрывающую анимацию и ничего нового не видит.
      // Выигрыш называется и голосом: звук у него свой на каждый размер, см. GDD, раздел 3.4.
      // Крупный тут молчит — его называют створки, и звук идёт вместе с ними.
      const heard: SfxName | null = big ? null : winSfx(payout, bet);
      if (heard !== null)
        sfx(heard);

      const shown: Promise<void> = wins.play(spin, !big);
      const featured: Promise<void> = feature?.afterSpin(spin) ?? Promise.resolve();

      await Promise.all([shown, featured]);

      // Показ мог оборваться тем же нажатием на спин: раунд кончился вместе с ним.
      if (cut)
        break;

      // Крупный выигрыш игра называет дважды подряд: сперва попапом поверх поля, потом
      // створками. Линии показа крутятся всё это время под заглушкой, как и на объявлении
      // фичи. Своей роли кнопке тут не нужно: она уже несёт скип показа или спин, и оба
      // проходят сумму разом.
      if (big)
        await callBigWin(payout);

      if (cut)
        break;

      // Выигрыш спина идёт в полосу сразу за его показом: игрок только что досмотрел сумму
      // на поле или на створках, и полоса называет её тем же движением. Ждать конца раунда
      // ей незачем — цепочка респинов идёт секунды, см. game/wallet.ts.
      wallet.win(payout);
    }

    // Что меняется раз за раунд, показывается в его конце: смена стадии видна после
    // последнего респина, а не между ними, см. GDD, раздел 5.1. Кнопка тут уже отдала спин,
    // и нажатие на неё обрывает показ надстройки вместе со всем раундом.
    if (!cut)
      await feature?.afterRound(result);

    // Счёт встаёт по остатку с бэкенда: деньги считает он, а не клиент, см. ADR-09. Сложенные
    // по спинам выигрыши с остатком сходятся, а выплату бонуса он приносит сам, и потому идёт
    // после показа надстройки: бонус играет она, и до этой строки игрок его уже досмотрел.
    //
    // Идёт это и на оборванном раунде: показ игрок оборвал, а деньги за него уже прошли.
    wallet.settle(result);

    machine.goTo('idle');

    // Счёт мог кончиться этим раундом: тогда кнопке нечего предлагать, и она гаснет.
    controls.showSpin(wallet.affords);

    /**
     * Затяжка остановки под анимацию ожидания на этом спине.
     *
     * Показ идёт не с заказа остановки, а с той минуты, когда барабаны перед ним уже встали:
     * до неё игрок ещё не прочитал скаттеры, и рамка вылезла бы раньше того, чего она ждёт.
     * Эту минуту называют сами барабаны, см. ReelHold.
     */
    function anticipate(spin: SpinResult): ReelHold | null {
      if (anticipation === null)
        return null;

      const reel: number | null = anticipation.reelFor(spin);
      if (reel === null)
        return null;

      // Рамка падает на барабан и шипит паром: звук она несёт свой, см. ui/anticipation.ts.
      return { reel, seconds: anticipation.seconds, onAlone: () => anticipation.show(reel) };
    }

    /** Проходит разом показ итога спина вместе с показом надстройки и суммой на створках. */
    function skipShow(): void {
      wins.stop();
      cover.skip();
      bigWin?.skip();
      feature?.skip();
    }

    /**
     * Называет крупный выигрыш: сперва попап поверх поля, следом сумма на створках.
     *
     * Попап уходит сам, и только за ним сходятся створки: две суммы разом читались бы
     * как две выплаты, см. ADR-56. Звук идёт с попапом, а не со створками: игрок слышит
     * крупный выигрыш в тот же миг, когда видит его.
     *
     * Оборванный показ до створок не доходит: игрок уже начал следующий раунд.
     */
    async function callBigWin(payout: Cents): Promise<void> {
      sfx('bigWin');

      await (bigWin?.show(text('bigWin'), payout) ?? Promise.resolve());

      if (!cut)
        await cover.bigWin(text('bigWin'), payout);
    }

    /**
     * Объявляет фичу: поле закрывается заглушкой с её именем и снова открывается.
     *
     * Показ прошлого спина крутит свои линии всё это время, под заглушкой. Снять его здесь
     * значило бы погасить линии у игрока на глазах, за миг до того, как створки закроют поле.
     * Его снимет респин, разом с пуском барабанов.
     *
     * Скип проходит объявление, как проходит любую другую ступень раунда.
     */
    async function announce(): Promise<void> {
      offerSkip(() => cover.skip());

      await cover.announce(text('nudgeRespin'));
    }

    /**
     * Ведёт бесплатный респин: барабаны с wild съезжают на символ вниз, остальные крутятся.
     *
     * Итог респина известен с ответа бэкенда, но выдержку он идёт полную: игрок читает
     * прокрутку так же, как на оплаченном спине. Скип обрывает её, как и там.
     */
    async function respin(spin: SpinResult): Promise<void> {
      machine.goTo('spinning');

      // Показ прошлого спина крутит свои линии, а барабанам под ним уже ехать.
      wins.stop();

      reels.start(spin.nudged);
      reels.nudge(spin);

      // Звучит респин тем же пуском барабанов, что и оплаченный спин: своего голоса у него
      // нет. Гудок паровоза — отклик на выигрыш, и обещать его каждым респином нельзя,
      // см. ADR-49.
      sfx('spin');

      const wait: Countdown = countdown(HOLD_MS);
      offerSkip(wait.finish);

      await wait.finished;
    }

    /**
     * Просит у бэкенда итог раунда, пока не получит его.
     *
     * Номер раунда один на все попытки: по нему бэкенд отдаёт итог уже сыгранного раунда,
     * поэтому повтор не крутит новый спин и не стоит игроку ничего.
     */
    async function ask(): Promise<RoundResult> {
      const id: string = crypto.randomUUID();

      for (;;) {
        try {
          return await parts.ask(id, bet);
        } catch (error: unknown) {
          console.error('Запрос спина не удался', error);

          // Кнопка на время сообщения не нужна: торопить нечего, итога всё равно нет.
          controls.showSkip(null);
          await notice.ask(text('noAnswer'));
          controls.showSkip(hurry);
        }
      }
    }
  }
}

/**
 * Ведёт раунды спина: одновременно идёт ровно один.
 *
 * Нажатие на показе итога обрывает раунд и дожидается его конца, а следующий раунд начинается
 * штатно, с чистого поля. Дожидаться нужно: пока прежний раунд не вернул игру в ожидание,
 * начать новый нельзя.
 */
export class SpinRounds {
  readonly #parts: SpinParts;
  readonly #onRoundFinished: () => void;

  #round: SpinRound | null = null;
  #running = false;
  #pending = false;

  constructor(parts: SpinParts, onRoundFinished: () => void = () => {}) {
    this.#parts = parts;
    this.#onRoundFinished = onRoundFinished;
  }

  /**
   * Ведёт раунд по нажатию кнопки спина.
   *
   * Нажатие всегда значит одно: крутить. Торопить идущий раунд кнопка предлагает под своим
   * лицом, скипом, и до спина она доходит только на показе выигрыша, см. runSpinRound.
   *
   * Счёта меньше ставки — раунд не начинается вовсе. Кнопка в эту минуту погашена, но нажать
   * на неё можно с клавиатуры, и запрос ушёл бы бэкенду только за отказом.
   */
  spin(): void {
    if (!this.#parts.wallet.affords)
      return;

    this.#pending = true;
    this.#round?.cut();

    if (this.#running)
      return;

    void this.#drain().catch((error: unknown) => {
      console.error('Раунд спина сорвался', error);
    });
  }

  /** Последовательно исполняет заказанные спины. Повторный заказ обрывает только текущий. */
  async #drain(): Promise<void> {
    this.#running = true;
    try {
      while (this.#pending) {
        this.#pending = false;

        const round: SpinRound = runSpinRound(this.#parts);
        this.#round = round;
        await round.finished;
        this.#round = null;

        // На обычном конце autoplay может стать возможным лишь после settle. Если уже
        // заказан следующий спин, этот вызов не нужен: цикл тут же перейдёт к нему.
        if (!this.#pending)
          this.#onRoundFinished();
      }
    } finally {
      this.#running = false;
    }
  }
}
